import React from "react";
import styled from "styled-components";
import { Tab, Tooltip } from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";
import { ColorUtilities } from "../../rule-engine-core/Common/ColorUtilities";
import type { AppGlueAny } from "../../rule-engine-core/Common/AppGlueAny";

export interface SidebarItem {
    renderUI: () => JSX.Element | null;
    value: string;
    icon?: JSX.Element;
    label?: string;
}

export interface SidebarProps {
    items: SidebarItem[];
    value: string;
    onChange: (value: string) => void;
}

export class Sidebar extends React.Component<SidebarProps> {
    render() {
        return (
            <TabWrapper>
                <TabContext value={this.props.value}>
                    <StyledTabList
                        orientation="vertical"
                        onChange={(_event: AppGlueAny, mode: string) => this.props.onChange(mode)}
                        classes={{
                            indicator: "SidebarTab-indicator",
                            root: "SidebarTabs",
                            scroller: "SidebarScroller",
                        }}
                    >
                        {this.props.items.map((d) => (
                            <StyledTab
                                label={
                                    <TabLabelWithIcon
                                        label={d.label || d.value}
                                        icon={d.icon}
                                    />
                                }
                                classes={{ root: "SidebarTab-root", selected: "SidebarTab-selected" }}
                                value={d.value}
                                key={d.value}
                                data-testid={d.label || d.value}
                            />
                        ))}
                    </StyledTabList>
                    <div style={{ display: "flex", flex: 1, overflow: "auto", position: "relative" }}>
                        {this.props.items.map((d) => (
                            <StyledTabPanel
                                value={d.value}
                                key={d.value}
                            >
                                {d.renderUI()}
                            </StyledTabPanel>
                        ))}
                    </div>
                </TabContext>
            </TabWrapper>
        );
    }
}

const StyledTabList = styled(TabList)`
    .SidebarScroller {
        background: ${UIStyleConstants.BLUE_COLOR};
        padding: 16px;
    }
`;

const StyledTab = styled(Tab)`
    && {
        min-width: 50px;
        width: 50px;
        height: 50px;

        font-style: normal;
        font-weight: 500;
        font-size: 10px;
        line-height: 140%;
        color: ${UIStyleConstants.GRAY_LIGHT_COLOR};
        text-align: center;
        border-radius: 4px;

        &.SidebarTab-selected {
            background: ${ColorUtilities.fade(UIStyleConstants.BLUE_LIGHTER_COLOR, 0.8)};
            color: ${UIStyleConstants.WHITE_COLOR};
        }

        @media (min-width: 1366px) {
            min-width: calc(70px + 7 * (100vw - 1366px) / 554);
            width: calc(70px + 7 * (100vw - 1366px) / 554);
            height: auto;
            padding: calc(8px + 3 * (100vw - 1366px) / 554) 0;
        }

        @media (min-width: 1920px) {
            width: 84px;
            min-width: 84px;
            padding: 12px 0;
        }

        .SidebarTabWrapper {
            display: flex;
            flex-flow: column;
            align-items: center;

            img {
                margin-bottom: 0px;
                @media (min-width: 1366px) {
                    margin-bottom: 4px;
                }
            }

            span {
                display: none;

                @media (min-width: 1366px) {
                    display: inline;
                }
            }
        }
    }
`;

const StyledTabPanel = styled(TabPanel)`
    && {
        &:not([hidden]) {
            padding: 0;
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            overflow: hidden;
            display: flex;
        }
    }
`;

const TabWrapper = styled.div`
    position: relative;
    display: flex;
    width: 100%;
    overflow: hidden;
    flex: 1;
`;

const TabLabelWithIcon: React.FC<{ icon?: JSX.Element | null; label: string }> = (props) => {
    return (
        <Tooltip
            title={props.label}
            placement="top"
            arrow
        >
            <div className="SidebarTabWrapper">
                {props.icon}
                <span>{props.label}</span>
            </div>
        </Tooltip>
    );
};
